/**
 * Enum defining the available math practice methods
 */
export enum PracticeMethods {
  Addition = "Addition",
  AdditionSubtraction = "AdditionSubtraction",
  Multiplication = "Multiplication",
  Division = "Division",
}

export const getRandomNumberInRange = (
  positive: boolean,
  min: number,
  max: number
): number => {
  const randomNum = Math.floor(Math.random() * (max - min + 1)) + min;
  if (!positive) {
    return Math.random() < 0.5 ? randomNum : -randomNum;
  }
  return randomNum;
};

/**
 * Generates a random number with a specified number of digits
 * @param positive - If true, returns a positive number; if false, returns a positive or negative number
 * @param digits - Number of digits in the generated number (must be >= 1)
 * @returns A random number with the specified number of digits
 * @example getRandomNumber(true, 3) returns a number between 100 and 999
 */
export const getRandomNumber = (positive: boolean, digits: number): number => {
  // Calculate upper bound (e.g., for 3 digits: 999)
  const upper = Math.pow(10, digits) - 1;
  // Calculate lower bound (e.g., for 3 digits: 100)
  const lower = Math.pow(10, digits - 1);

  // Generate random number in range [lower, upper]
  const randomNum = Math.floor(Math.random() * (upper - lower + 1)) + lower;

  // If negative numbers allowed, randomly make it negative
  if (!positive) {
    return Math.random() < 0.5 ? randomNum : -randomNum;
  }

  return randomNum;
};

/**
 * Generates an array of random numbers for addition/subtraction problems
 * @param positive - If true, only positive numbers; if false, can include negative numbers
 * @param rows - Number of numbers to generate
 * @param digits - Number of digits for each number
 * @param operation - Operation type (e.g., "add", "sub", "add-sub")
 * @returns Array of random numbers
 */
export const getAddNumArray = (
  positive: boolean,
  rows: number,
  digits: number,
  operation: string,
  range?: { min: number; max: number }
): number[] => {
  const genNum = (isPositive: boolean) =>
    range
      ? getRandomNumberInRange(isPositive, range.min, range.max)
      : getRandomNumber(isPositive, digits);

  const numArray: number[] = [];

  // First number is always positive to ensure a positive starting point
  numArray.push(genNum(true));

  // Generate remaining numbers
  for (let i = 1; i < rows; i++) {
    let newNumber: number;
    let isValid = false;
    let attempts = 0;
    const maxAttempts = 1000; // Safety limit to prevent infinite loops

    // Keep generating until we get a valid number
    while (!isValid && attempts < maxAttempts) {
      attempts++;
      newNumber = genNum(positive);

      // Check if number is different from the previous one
      const isDifferentFromPrevious =
        newNumber !== numArray[numArray.length - 1];

      if (operation.includes("sub")) {
        // For subtraction, ensure the running total doesn't go negative
        const currentSum = numArray.reduce((sum, num) => sum + num, 0);
        const wouldBeNegative = newNumber < 0 && currentSum + newNumber < 0;
        isValid = isDifferentFromPrevious && !wouldBeNegative;
      } else {
        isValid = isDifferentFromPrevious;
      }
    }

    if (attempts >= maxAttempts) {
      console.error(
        `[getAddNumArray] Failed to generate valid number ${i + 1} after ${maxAttempts} attempts`
      );
      // Fallback: use a simple increment to ensure we get a different number
      newNumber = numArray[numArray.length - 1] + 1;
    }

    numArray.push(newNumber!);
  }

  return numArray;
};

/**
 * Generates two random numbers for multiplication problems
 * Both numbers will be greater than 1 to avoid trivial multiplications
 * @param digits1 - Number of digits for the first number
 * @param digits2 - Number of digits for the second number
 * @returns Array of two numbers [multiplicand, multiplier]
 */
export const getMulNumArray = (
  digits1: number,
  digits2: number,
  range?: { min: number; max: number }
): number[] => {
  const gen1 = () =>
    range
      ? getRandomNumberInRange(true, range.min, range.max)
      : getRandomNumber(true, digits1);
  const gen2 = () =>
    range
      ? getRandomNumberInRange(true, range.min, range.max)
      : getRandomNumber(true, digits2);

  let num1 = gen1();
  while (num1 <= 1) {
    num1 = gen1();
  }

  let num2 = gen2();
  while (num2 <= 1) {
    num2 = gen2();
  }

  return [num1, num2];
};

/**
 * Generates two random numbers for division problems
 * Ensures the first number is evenly divisible by the second (no remainder)
 * Also ensures the divisor is not 1 to avoid trivial divisions
 * @param digits1 - Number of digits for the dividend
 * @param digits2 - Number of digits for the divisor
 * @returns Array of two numbers [dividend, divisor]
 */
export const getDivNumArray = (
  digits1: number,
  digits2: number,
  range?: { min: number; max: number }
): number[] => {
  let dividend: number;
  let divisor: number;

  if (range) {
    do {
      dividend = getRandomNumberInRange(true, range.min, range.max);
      divisor = getRandomNumberInRange(true, range.min, range.max);
    } while (dividend % divisor !== 0 || divisor === 1);
  } else {
    const minDividend = Math.pow(10, digits1 - 1);
    const maxDividend = Math.pow(10, digits1) - 1;
    const minDivisor = Math.pow(10, digits2 - 1);
    const maxDivisor = Math.pow(10, digits2) - 1;

    do {
      dividend =
        Math.floor(Math.random() * (maxDividend - minDividend + 1)) +
        minDividend;
      divisor =
        Math.floor(Math.random() * (maxDivisor - minDivisor + 1)) + minDivisor;
    } while (dividend % divisor !== 0 || divisor === 1);
  }

  return [dividend, divisor];
};

/**
 * Calculates the correct answer for a given question based on the practice method
 * @param question - Array of numbers representing the problem
 * @param practiceMethod - The type of math operation to perform
 * @returns The correct answer
 */
export const getCorrectAnswer = (
  question: number[],
  practiceMethod: PracticeMethods
): number => {
  switch (practiceMethod) {
    case PracticeMethods.Addition:
    case PracticeMethods.AdditionSubtraction:
      // Sum all numbers in the array
      return question.reduce((sum, num) => sum + num, 0);

    case PracticeMethods.Multiplication:
      return question[0] * question[1];

    case PracticeMethods.Division:
      return question[0] / question[1];
  }
};
